import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);

export default class ContactAnimations {
    constructor() {
        this.isInitialized = false;
        this.currentPanel = 'kontakt';
        this.isAnimating = false;
        this.currentAnimationTimeline = null;
        this.init();
    }

    init() {
        if (this.isInitialized) return;

        // Setup animations and content management
        this.setupTopLineColorAdjustments();
        this.setupBottomLineVisibility();
        this.setupContentAnimations();

        this.isInitialized = true;
    }

    setupTopLineColorAdjustments() {
        // Animate the top line colors when scrolling into contact section
        ScrollTrigger.create({
            trigger: "#contact",
            start: "top top",
            end: "+=100%",
            scrub: true,
            onUpdate: (self) => {
                // Get current theme colors
                const isDarkMode = document.body.classList.contains("dark-mode");
                const bgColor = getComputedStyle(document.documentElement)
                    .getPropertyValue("--bg-color")
                    .trim();

                // Get the original colors from CSS variables
                const originalLineColor = isDarkMode
                    ? "rgba(255, 255, 255, 0.4)"
                    : "rgba(0, 0, 0, 0.7)";
                const originalTextColor = isDarkMode
                    ? "rgb(224, 224, 224)"
                    : "rgb(44, 44, 44)";
                const targetColor = isDarkMode ? "#000000" : bgColor;

                // Animate top line background
                gsap.to(".top-horizontal-line", {
                    backgroundColor: gsap.utils.interpolate(
                        originalLineColor,
                        targetColor,
                        self.progress
                    ),
                    overwrite: "auto",
                    duration: 0,
                });

                // Animate text color
                gsap.to([
                    ".top-line-text",
                    ".top-line-text-right",
                    ".menu-toggle",
                    ".scroll-text",
                ], {
                    color: gsap.utils.interpolate(
                        originalTextColor,
                        targetColor,
                        self.progress
                    ),
                    overwrite: "auto",
                    duration: 0,
                });

                // Animate scroll indicator line color
                gsap.to(".scroll-line", {
                    backgroundColor: gsap.utils.interpolate(
                        originalLineColor,
                        targetColor,
                        self.progress
                    ),
                    overwrite: "auto",
                    duration: 0,
                });
            }
        });
    }

    setupBottomLineVisibility() {
        // Show bottom line when reaching the bottom of the contact section
        ScrollTrigger.create({
            trigger: "#contact",
            start: "bottom bottom+=100", // Show when contact bottom is 100px from viewport bottom
            end: "bottom top", // Hide when contact bottom reaches viewport top
            onEnter: () => {
                document.querySelector('.bottom-horizontal-line').classList.add('visible');
            },
            onLeave: () => {
                document.querySelector('.bottom-horizontal-line').classList.remove('visible');
            },
            onEnterBack: () => {
                document.querySelector('.bottom-horizontal-line').classList.add('visible');
            },
            onLeaveBack: () => {
                document.querySelector('.bottom-horizontal-line').classList.remove('visible');
            }
        });
    }

    setupContentAnimations() {
        // Setup button event listeners for content switching
        this.setupFooterButtonListeners();
    }

    setupFooterButtonListeners() {
        const footerButtons = document.querySelectorAll('.footer-btn');

        footerButtons.forEach(button => {
            button.addEventListener('click', (e) => {
                const targetContent = e.target.dataset.content;
                if (targetContent && targetContent !== this.currentPanel && !this.isAnimating) {
                    this.switchContentSimple(targetContent);
                }
            });
        });
    }

    switchContentSimple(targetPanel) {
        if (this.isAnimating) return;

        this.isAnimating = true;

        const currentContent = document.querySelector(`[data-panel="${this.currentPanel}"]`);
        const targetContent = document.querySelector(`[data-panel="${targetPanel}"]`);
        const currentButton = document.querySelector(`[data-content="${this.currentPanel}"]`);
        const targetButton = document.querySelector(`[data-content="${targetPanel}"]`);

        if (!currentContent || !targetContent || !currentButton || !targetButton) {
            console.error('Content or button not found');
            this.isAnimating = false;
            return;
        }

        // Slide and fade out current content
        gsap.to(currentContent, {
            opacity: 0,
            y: -20,
            duration: 0.25,
            ease: "power2.out",
            onComplete: () => {
                // Update button states
                currentButton.classList.remove('active');
                targetButton.classList.add('active');

                // Switch content
                currentContent.classList.remove('active');
                targetContent.classList.add('active');

                // Update current panel
                this.currentPanel = targetPanel;

                // Slide and fade in new content from below
                gsap.fromTo(targetContent,
                    { opacity: 0, y: 20 },
                    {
                        opacity: 1,
                        y: 0,
                        duration: 0.25,
                        ease: "power2.out",
                        onComplete: () => {
                            this.isAnimating = false;
                        }
                    }
                );
            }
        });
    }

    destroy() {
        // Kill all ScrollTriggers related to contact section
        ScrollTrigger.getAll().forEach(trigger => {
            if (trigger.trigger && trigger.trigger.closest('#contact')) {
                trigger.kill();
            }
        });

        // Kill current animation timeline
        if (this.currentAnimationTimeline) {
            this.currentAnimationTimeline.kill();
            this.currentAnimationTimeline = null;
        }

        // Remove event listeners
        const footerButtons = document.querySelectorAll('.footer-btn');
        footerButtons.forEach(button => {
            button.removeEventListener('click', this.switchContentSimple);
        });

        this.isInitialized = false;
        this.isAnimating = false;
    }
}
